import React from 'react';
import { Box, Button as MuiButton, Drawer, LinearProgress, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { closeMiniCart } from '../../features/cartSlice';
import { ROUTES } from '../../constants';
import { formatCurrency, getEffectivePrice } from '../../utils/pricing';
import { resolveImageUrl } from '../../utils/image';
import useStoreSettings from '../../hooks/useStoreSettings';

const MiniCartDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { cart, guestItems, miniCartOpen } = useAppSelector((state) => state.cart);
  const { token } = useAppSelector((state) => state.auth);

  const serverItems = (cart?.items || []).filter((item) => Boolean(item.product));
  const items = token ? serverItems : guestItems.filter((item) => Boolean(item.product));
  const lastItem = items[items.length - 1];
  const subtotal = items.reduce(
    (sum, item) => sum + getEffectivePrice(item.product) * item.quantity,
    0
  );
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const store = useStoreSettings();
  const shippingNote = !store
    ? null
    : store.shippingCharge <= 0
      ? 'Free shipping on all orders'
      : store.freeShippingThreshold > 0 && subtotal >= store.freeShippingThreshold
        ? 'Free delivery unlocked 🎉'
        : store.freeShippingThreshold > 0
          ? `Add ${formatCurrency(store.freeShippingThreshold - subtotal)} more for free delivery`
          : `Delivery ${formatCurrency(store.shippingCharge)} at checkout`;

  return (
    <Drawer
      anchor="right"
      open={miniCartOpen}
      onClose={() => dispatch(closeMiniCart())}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 380 }, p: 3 } }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Added to cart
      </Typography>

      {lastItem && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box
            component="img"
            src={resolveImageUrl(lastItem.product.images?.[0] || '')}
            alt={lastItem.product.name}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 2 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {lastItem.product.name}
            </Typography>
            <Typography variant="body2" color="primary">
              {formatCurrency(getEffectivePrice(lastItem.product))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Qty: {lastItem.quantity}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Cart ({totalItems} {totalItems === 1 ? 'item' : 'items'})
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(subtotal)}
          </Typography>
        </Box>
        {store && store.shippingCharge > 0 && store.freeShippingThreshold > 0 ? (
          subtotal >= store.freeShippingThreshold ? (
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
              🎉 You've unlocked FREE shipping!
            </Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                🎉 Only {formatCurrency(store.freeShippingThreshold - subtotal)} away from FREE
                shipping!
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (subtotal / store.freeShippingThreshold) * 100)}
                sx={{
                  height: 6,
                  borderRadius: 99,
                  backgroundColor: 'rgba(43,37,43,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background: 'linear-gradient(90deg, #D65A88, #7061DA)'
                  }
                }}
              />
            </>
          )
        ) : (
          shippingNote && (
            <Typography
              variant="caption"
              color={shippingNote.startsWith('Free') ? 'success.main' : 'text.secondary'}
            >
              {shippingNote}
            </Typography>
          )
        )}
      </Box>

      <MuiButton
        fullWidth
        variant="contained"
        color="primary"
        component={RouterLink}
        to={ROUTES.CHECKOUT}
        onClick={() => dispatch(closeMiniCart())}
        sx={{ mb: 1.5 }}
      >
        Checkout
      </MuiButton>
      <MuiButton
        fullWidth
        variant="outlined"
        component={RouterLink}
        to={ROUTES.CART}
        onClick={() => dispatch(closeMiniCart())}
        sx={{ mb: 1.5 }}
      >
        View cart
      </MuiButton>
      <MuiButton
        fullWidth
        variant="text"
        onClick={() => dispatch(closeMiniCart())}
      >
        Continue shopping
      </MuiButton>
    </Drawer>
  );
};

export default MiniCartDrawer;
